import { useState } from "react";
import { toast } from "sonner";
import { Download } from "lucide-react";
import { PageHead, TabBar } from "../components/PageHead";
import { api, errMsg, useApi, fmtDate, downloadFile } from "../lib/api";
import { SampleBadge, Decision } from "../components/Badges";

const STAGES = ["received", "under_review", "shortlisted", "interview", "decision_hired", "decision_declined"];
const label = (s) => s.replace("decision_", "decision: ").replace("_", " ");

const Pipeline = () => {
  const { data, reload } = useApi("/applications");
  const move = async (id, status) => {
    try {
      await api.patch(`/applications/${id}`, { status });
      toast.success(`Moved to ${label(status)}`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div data-testid="hr-pipeline">
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mb-6">
        {STAGES.map((s) => (
          <div key={s} className="panel p-3" data-testid={`pipeline-count-${s}`}>
            <div className="font-data text-2xl text-ink">{(data || []).filter((a) => a.status === s).length}</div>
            <div className="font-data text-[10px] uppercase text-slate-500">{label(s)}</div>
          </div>
        ))}
      </div>
      <div className="panel overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead><tr className="text-left text-slate-500 text-xs border-b border-[color:var(--line)]"><th className="p-3">Candidate</th><th>Role</th><th>Applied</th><th>Status</th><th>CV</th></tr></thead>
          <tbody>
            {(data || []).map((a) => (
              <tr key={a.id} className="border-b border-[color:var(--line)]/50" data-testid={`application-row-${a.id}`}>
                <td className="p-3"><div className="text-ink">{a.name}</div><div className="text-[11px] text-slate-500">{a.email} · {a.phone} · {a.code}</div></td>
                <td className="text-slate-300">{a.job_title}<div className="text-[11px] text-slate-500">{a.department}</div></td>
                <td className="font-data text-xs text-slate-400">{fmtDate(a.created_at)}</td>
                <td>
                  <select className="field !py-1 !text-xs !w-auto" value={a.status} onChange={(e) => move(a.id, e.target.value)} data-testid={`application-status-${a.id}`}>
                    {STAGES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
                  </select>
                </td>
                <td>{a.cv && <button onClick={() => downloadFile(`/applications/${a.id}/cv`, a.cv.filename).catch((e) => toast.error(errMsg(e)))} className="text-gold" data-testid={`application-cv-${a.id}`}><Download size={16} /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {data?.length === 0 && <div className="p-6 text-sm text-slate-500">No applications yet. Share the careers page: /careers</div>}
      </div>
      <Decision>Move candidates through Received → Review → Shortlist → Interview → Decision; candidates see every change instantly.</Decision>
    </div>
  );
};

const JobsAdmin = () => {
  const { data, reload } = useApi("/public/jobs");
  const blank = { title: "", entity: "Despite Music Production Ltd.", brand: "Peace FM", department: "Newsroom", location: "Accra", language: "Asante Twi + English", type: "Full-time", requirements: "" };
  const [f, setF] = useState(blank);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/jobs", f);
      toast.success("Role published on the careers portal");
      setF(blank);
      reload();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const close = async (id) => {
    await api.patch(`/jobs/${id}`, { status: "closed" });
    toast.success("Role closed");
    reload();
  };
  return (
    <div className="grid lg:grid-cols-3 gap-6" data-testid="jobs-admin">
      <form onSubmit={submit} className="panel p-5 grid gap-3 h-fit" data-testid="job-create-form">
        <div className="eyebrow">Post a role</div>
        <input required className="field" placeholder="Job title" value={f.title} onChange={set("title")} data-testid="job-title-input" />
        <input required className="field" placeholder="Registered entity" value={f.entity} onChange={set("entity")} data-testid="job-entity-input" />
        <div className="grid grid-cols-2 gap-3">
          <input required className="field" placeholder="Brand" value={f.brand} onChange={set("brand")} data-testid="job-brand-input" />
          <input required className="field" placeholder="Department" value={f.department} onChange={set("department")} data-testid="job-department-input" />
          <input required className="field" placeholder="Location" value={f.location} onChange={set("location")} data-testid="job-location-input" />
          <input required className="field" placeholder="Language" value={f.language} onChange={set("language")} data-testid="job-language-input" />
        </div>
        <textarea required rows={3} className="field" placeholder="Requirements & qualifications" value={f.requirements} onChange={set("requirements")} data-testid="job-requirements-input" />
        <button className="btn-gold" data-testid="job-create-btn">Publish role</button>
      </form>
      <div className="lg:col-span-2 space-y-2">
        {(data || []).map((j) => (
          <div key={j.id} className="panel p-4 flex justify-between items-center gap-3" data-testid={`admin-job-${j.id}`}>
            <div><div className="text-ink">{j.title} {j.is_sample && <SampleBadge label="Sample" />}</div><div className="text-[11px] text-slate-500">{j.brand} · {j.department} · {j.location}</div></div>
            <button onClick={() => close(j.id)} className="btn-ghost !py-1.5 !text-xs" data-testid={`job-close-btn-${j.id}`}>Close</button>
          </div>
        ))}
      </div>
    </div>
  );
};

const Pool = () => {
  const [q, setQ] = useState("");
  const { data } = useApi(`/talent-pool${q ? `?q=${encodeURIComponent(q)}` : ""}`);
  return (
    <div data-testid="talent-pool-admin">
      <input className="field mb-4 max-w-md" placeholder="Search skills, names, interests…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="pool-search-input" />
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3">
        {(data || []).map((p) => (
          <div key={p.id} className="panel p-4" data-testid={`pool-card-${p.id}`}>
            <div className="flex justify-between"><div className="text-ink">{p.name}</div>{p.cv && <button onClick={() => downloadFile(`/applications/${p.id}/cv`, p.cv.filename).catch((e) => toast.error(errMsg(e)))} className="text-gold" data-testid={`pool-cv-${p.id}`}><Download size={15} /></button>}</div>
            <div className="text-[11px] text-slate-500">{p.email} · {p.interests} · {p.languages}</div>
            <div className="flex flex-wrap gap-1 mt-2">{(p.skills || []).map((s) => <span key={s} className="pill pill-slate">{s}</span>)}</div>
          </div>
        ))}
        {data?.length === 0 && <div className="text-sm text-slate-500">Talent pool is empty — it fills as Ghanaians join via /careers.</div>}
      </div>
    </div>
  );
};

const Inbox = () => {
  const { data: fb } = useApi("/feedback");
  const { data: bk } = useApi("/museum-bookings");
  const { data: od } = useApi("/orders");
  const { data: pt } = useApi("/partners");
  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="public-inbox">
      <div className="panel p-5"><div className="eyebrow mb-3">Listener feedback (auto-routed)</div>
        {(fb || []).map((f) => <div key={f.id} className="py-2 border-b border-[color:var(--line)]/50 text-sm"><span className="pill pill-gold mr-2">{f.routed_to}</span><span className="text-slate-300">{f.message}</span><div className="text-[10px] text-slate-500 font-data">{f.name} · {f.station} · {fmtDate(f.created_at)}</div></div>)}
        {fb?.length === 0 && <div className="text-sm text-slate-500">No feedback yet.</div>}
      </div>
      <div className="panel p-5"><div className="eyebrow mb-3">Museum visit requests</div>
        {(bk || []).map((b) => <div key={b.id} className="py-2 border-b border-[color:var(--line)]/50 text-sm text-slate-300">{b.ref} · {b.name} · {b.visit_date} · {b.group_size} pax <div className="text-[10px] text-slate-500">{b.phone}</div></div>)}
        {bk?.length === 0 && <div className="text-sm text-slate-500">No booking requests yet.</div>}
      </div>
      <div className="panel p-5"><div className="eyebrow mb-3">Marketplace order requests</div>
        {(od || []).map((o) => <div key={o.id} className="py-2 border-b border-[color:var(--line)]/50 text-sm text-slate-300">{o.ref} · {o.product} × {o.quantity} · {o.channel}<div className="text-[10px] text-slate-500">{o.name} · {o.phone} · {o.location}</div></div>)}
        {od?.length === 0 && <div className="text-sm text-slate-500">No orders yet.</div>}
      </div>
      <div className="panel p-5"><div className="eyebrow mb-3">Supplier & distributor applications</div>
        {(pt || []).map((p) => <div key={p.id} className="py-2 border-b border-[color:var(--line)]/50 text-sm text-slate-300">{p.ref} · {p.company} · {p.type.replace("_", " ")} · {p.region}<div className="text-[10px] text-slate-500">{p.contact} · {p.phone} · {p.brands}</div></div>)}
        {pt?.length === 0 && <div className="text-sm text-slate-500">No applications yet.</div>}
      </div>
    </div>
  );
};

const TABS = [["pipeline", "Application Pipeline"], ["jobs", "Job Postings"], ["pool", "Talent Pool"], ["inbox", "Public Inbox"]];
const VIEWS = { pipeline: Pipeline, jobs: JobsAdmin, pool: Pool, inbox: Inbox };

export default function HR() {
  const [tab, setTab] = useState("pipeline");
  const View = VIEWS[tab];
  return (
    <div data-testid="hr-portal">
      <PageHead eyebrow="Portal C · Recruitment & Talent (HR)" title="Talent & HR">Strategic talent acquisition across all DGC entities.</PageHead>
      <TabBar tabs={TABS} value={tab} onChange={setTab} />
      <div className="p-5 md:p-10"><View /></div>
    </div>
  );
}
